import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { FileText, Clock, Target, Award } from 'lucide-react';

interface LiquidityMetricsCardsProps {
  avgQuotesPerRfq: number;
  rfqsWith3PlusQuotes: number;
  rfqsWith3PlusQuotesPercent: number;
  avgTimeToFirstQuoteHours: number;
  rfqsWithAward: number;
  rfqsWithAwardPercent: number;
  totalRfqs: number;
  isLoading: boolean;
}

export function LiquidityMetricsCards({
  avgQuotesPerRfq,
  rfqsWith3PlusQuotes,
  rfqsWith3PlusQuotesPercent,
  avgTimeToFirstQuoteHours,
  rfqsWithAward,
  rfqsWithAwardPercent,
  totalRfqs,
  isLoading,
}: LiquidityMetricsCardsProps) {
  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Marketplace Liquidity</CardTitle>
          <CardDescription>How well supply matches demand</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-24" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  const formatHours = (hours: number) => {
    if (hours < 24) return `${hours}h`;
    const days = Math.floor(hours / 24);
    const remainingHours = hours % 24;
    return remainingHours > 0 ? `${days}d ${remainingHours}h` : `${days}d`;
  };

  const metrics = [
    {
      label: 'Avg Quotes per RFQ',
      value: avgQuotesPerRfq.toFixed(1),
      subtext: `${totalRfqs} total RFQs`,
      icon: FileText,
      status: avgQuotesPerRfq >= 3 ? 'good' : avgQuotesPerRfq >= 1 ? 'warning' : 'bad',
    },
    {
      label: 'RFQs with 3+ Quotes',
      value: `${rfqsWith3PlusQuotesPercent}%`,
      subtext: `${rfqsWith3PlusQuotes} of ${totalRfqs}`,
      icon: Target,
      status: rfqsWith3PlusQuotesPercent >= 50 ? 'good' : rfqsWith3PlusQuotesPercent >= 25 ? 'warning' : 'bad',
    },
    {
      label: 'Avg Time to First Quote',
      value: formatHours(avgTimeToFirstQuoteHours),
      subtext: 'after RFQ opens',
      icon: Clock,
      status: avgTimeToFirstQuoteHours <= 24 ? 'good' : avgTimeToFirstQuoteHours <= 72 ? 'warning' : 'bad',
    },
    {
      label: 'RFQ Award Rate',
      value: `${rfqsWithAwardPercent}%`,
      subtext: `${rfqsWithAward} awarded`,
      icon: Award,
      status: rfqsWithAwardPercent >= 60 ? 'good' : rfqsWithAwardPercent >= 30 ? 'warning' : 'bad',
    },
  ];

  const statusColors = {
    good: 'text-green-500',
    warning: 'text-amber-500',
    bad: 'text-red-500',
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Marketplace Liquidity</CardTitle>
        <CardDescription>How well supply matches demand</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {metrics.map((metric) => {
            const Icon = metric.icon;
            return (
              <div
                key={metric.label}
                className="p-4 rounded-lg border bg-muted/30"
              >
                <div className="flex items-center gap-2 mb-2">
                  <Icon className="h-4 w-4 text-muted-foreground" />
                  <span className="text-xs text-muted-foreground">{metric.label}</span>
                </div>
                <div className={`text-2xl font-bold ${statusColors[metric.status]}`}>
                  {metric.value}
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  {metric.subtext}
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
